import { formatPoolDenomination, type ProtocolPool } from "@zkresistor/shared";

export interface PoolShareData {
  title: string;
  text: string;
  url: string;
}

export type PoolShareResult = "shared" | "copied" | "cancelled";

const TELEGRAM_MINI_APP_URL = "https://t.me/ZkResistorBot/app";
const POOL_START_PARAM_PREFIX = "pool_";

export function poolStartParam(pool: Pick<ProtocolPool, "address">): string {
  return `${POOL_START_PARAM_PREFIX}${pool.address}`;
}

export function poolAddressFromStartParam(value: string | null | undefined): string | null {
  if (value === null || value === undefined || !value.startsWith(POOL_START_PARAM_PREFIX)) return null;
  const address = value.slice(POOL_START_PARAM_PREFIX.length);
  return address.length === 0 ? null : address;
}

function poolShareCopy(pool: ProtocolPool): Omit<PoolShareData, "url"> {
  const label = `${formatPoolDenomination(pool)} ${pool.symbol}`;
  return {
    title: `ZKResistor ${label} Pool`,
    text: `Open the ${label} privacy Pool on ZKResistor.`,
  };
}

export function poolShareData(pool: ProtocolPool, origin = window.location.origin): PoolShareData {
  const url = new URL("/", origin);
  url.searchParams.set("pool", pool.address);
  return { ...poolShareCopy(pool), url: url.toString() };
}

export function telegramPoolShareData(pool: ProtocolPool): PoolShareData {
  const url = new URL(TELEGRAM_MINI_APP_URL);
  url.searchParams.set("startapp", poolStartParam(pool));
  return { ...poolShareCopy(pool), url: url.toString() };
}

export function buildTelegramShareUrl(url: string, text: string): string {
  return `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
}

export async function sharePool(
  pool: ProtocolPool,
  origin = window.location.origin,
): Promise<PoolShareResult> {
  const data = poolShareData(pool, origin);
  const canShare = typeof navigator.share === "function" &&
    (typeof navigator.canShare !== "function" || navigator.canShare(data));
  if (canShare) {
    try {
      await navigator.share(data);
      return "shared";
    } catch (error) {
      if (typeof error === "object" && error !== null && "name" in error && error.name === "AbortError") {
        return "cancelled";
      }
    }
  }
  await navigator.clipboard.writeText(data.url);
  return "copied";
}
